import React, { useEffect, useState, useCallback } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FileUpload from "@/components/admin/FileUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ScheduleCalendar from "@/components/admin/journal/ScheduleCalendar";
import ReminderSettings, { LEAD_CHOICES } from "@/components/admin/journal/ReminderSettings";
import TemplatePicker from "@/components/admin/journal/TemplatePicker";
import DeliveryStatusChip from "@/components/admin/journal/DeliveryStatusChip";
import { marked } from "marked";
import { fmtDate, can, MANAGE_JOURNAL, JOURNAL_CATEGORIES } from "@/lib/format";
import { toast } from "sonner";
import { Plus, MoreHorizontal, BookOpen, Star, ExternalLink, Eye, EyeOff, CalendarClock, List, CalendarDays, BellRing, BookmarkPlus } from "lucide-react";

const EMPTY = {
  title: "", excerpt: "", body: "", body_format: "html", cover_image: "", author: "Beau Dugas", category: "Guides",
  tags: [], status: "draft", publish_at: "", reminder_lead_minutes: null, featured: false, seo_title: "", seo_description: "",
};

// Convert an ISO (UTC) datetime to a value for <input type="datetime-local"> in
// the editor's LOCAL timezone, and back. Editors pick a local time; we store UTC.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function localInputToIso(local) {
  if (!local) return "";
  const d = new Date(local); // parsed as local time
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
}

const STATUS_BADGE = {
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  draft: "bg-amber-100 text-amber-800 border-amber-200",
};
const STATUS_LABEL = { published: "Published", scheduled: "Scheduled", draft: "Draft" };

export default function AdminJournal() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_JOURNAL);
  const [items, setItems] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");            // "list" | "calendar"
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | scheduled | published
  const [selectedDay, setSelectedDay] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [tagsText, setTagsText] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/journal");
      setItems(r.data);
    } catch {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
    // Reminder-email delivery status (editors only; endpoint is RBAC-gated).
    if (editable) {
      try {
        const d = await api.get("/admin/journal/deliveries");
        setDeliveries(Array.isArray(d.data) ? d.data : []);
      } catch { /* non-fatal — chips simply won't show */ }
    }
  }, [editable]);
  useEffect(() => { load(); }, [load]);

  // Latest reminder delivery per article (list is sorted newest-first server-side).
  const deliveryByArticle = React.useMemo(() => {
    const map = {};
    for (const d of deliveries) {
      if (d.article_id && !map[d.article_id]) map[d.article_id] = d;
    }
    return map;
  }, [deliveries]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // "New Article" opens the starter-template picker first.
  const openNew = () => setPickerOpen(true);
  // Apply the editor's chosen starting point (null = blank article).
  const applyTemplate = (tpl) => {
    if (tpl) {
      setForm({
        ...EMPTY,
        title: tpl.title || "",
        category: JOURNAL_CATEGORIES.includes(tpl.category) ? tpl.category : EMPTY.category,
        excerpt: tpl.excerpt || "",
        cover_image: tpl.cover_image || "",
        body: tpl.body || "",
        body_format: "html",
      });
      toast.success(`Started from "${tpl.name}"`);
    } else {
      setForm({ ...EMPTY });
    }
    setTagsText("");
    setPickerOpen(false);
  };
  const openEdit = (a) => {
    // Legacy posts are Markdown; convert to HTML so they load into the visual editor.
    let body = a.body || "";
    if (a.body_format !== "html") {
      try { body = marked.parse(body); } catch { /* keep raw on failure */ }
    }
    setForm({ ...EMPTY, ...a, body, body_format: "html" });
    setTagsText((a.tags || []).join(", "));
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.status === "scheduled") {
      if (!form.publish_at) { toast.error("Pick a date & time to schedule this article"); return; }
      if (new Date(form.publish_at).getTime() <= Date.now()) { toast.error("Choose a publish time in the future"); return; }
    }
    const payload = {
      ...form,
      publish_at: form.status === "scheduled" ? form.publish_at : null,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (form.id) await api.put(`/admin/journal/${form.id}`, payload);
      else await api.post("/admin/journal", payload);
      toast.success(form.id ? "Article saved" : "Article created");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const togglePublish = async (a) => {
    const newStatus = a.status === "published" ? "draft" : "published";
    // Optimistic update for instant UI feedback
    setItems((prev) => prev.map((x) => x.id === a.id
      ? { ...x, status: newStatus, published_at: newStatus === "published" ? (x.published_at || new Date().toISOString()) : null }
      : x));
    try {
      await api.put(`/admin/journal/${a.id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Published" : "Moved to draft");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    load();
  };
  const toggleFeatured = async (a) => {
    const nf = !a.featured;
    setItems((prev) => prev.map((x) => x.id === a.id ? { ...x, featured: nf } : x));
    try {
      await api.put(`/admin/journal/${a.id}`, { featured: nf });
      toast.success(nf ? "Marked featured" : "Unfeatured");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    load();
  };
  const doDelete = () => act(() => api.delete(`/admin/journal/${toDelete.id}`), "Article deleted").then(() => setToDelete(null));

  const rescheduleArticle = async (id, iso) => {
    // Optimistic update, then persist.
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, publish_at: iso, reminder_sent: false } : x)));
    try {
      await api.put(`/admin/journal/${id}`, { status: "scheduled", publish_at: iso });
      toast.success("Rescheduled to " + new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    }
    load();
  };

  const bulkReschedule = async (items) => {
    try {
      const r = await api.post("/admin/journal/bulk-reschedule", { items });
      const okN = r.data?.updated?.length || 0;
      const errN = r.data?.errors?.length || 0;
      if (okN) toast.success(`Rescheduled ${okN} article${okN === 1 ? "" : "s"}`);
      if (errN) toast.error(`${errN} couldn't be moved (past date or not scheduled)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk reschedule failed");
    }
    load();
  };

  // Save the current editor content as a reusable custom template.
  const openSaveTpl = () => {
    if (!form) return;
    setTplName(form.title || "");
    setTplDesc("");
    setSaveTplOpen(true);
  };
  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("Give the template a name"); return; }
    setSavingTpl(true);
    try {
      await api.post("/admin/journal/templates", {
        name: tplName.trim(),
        description: tplDesc.trim(),
        category: form.category,
        title: form.title,
        excerpt: form.excerpt,
        cover_image: form.cover_image,
        body: form.body,
        body_format: form.body_format || "html",
      });
      toast.success("Saved as template — it's now available to all editors");
      setSaveTplOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save template");
    } finally {
      setSavingTpl(false);
    }
  };

  // Derived collections for filtering + the schedule calendar.
  const filtered = statusFilter === "all" ? items : items.filter((a) => a.status === statusFilter);
  const scheduledItems = items
    .filter((a) => a.status === "scheduled" && a.publish_at)
    .sort((a, b) => new Date(a.publish_at) - new Date(b.publish_at));
  const scheduledCount = scheduledItems.length;

  return (
    <div data-testid="admin-journal">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen className="text-[#c9a86a]" size={22} />
          <div>
            <h1 className="text-2xl font-semibold">Journal</h1>
            <p className="text-sm text-[var(--text-2)]">
              {items.length} article{items.length === 1 ? "" : "s"}
              {scheduledCount > 0 && <span className="text-[#9a7b3f]"> · {scheduledCount} scheduled</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle: List / Calendar */}
          <div className="inline-flex rounded-md border border-[var(--border-lch)] overflow-hidden" role="tablist" aria-label="View mode">
            <button
              onClick={() => setView("list")}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${view === "list" ? "bg-[var(--panel-2)] text-[#c9a86a]" : "text-[var(--text-2)] hover:bg-[var(--panel-2)]/50"}`}
              data-testid="journal-view-list"
              aria-selected={view === "list"}
            >
              <List size={15} /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-l border-[var(--border-lch)] transition-colors ${view === "calendar" ? "bg-[var(--panel-2)] text-[#c9a86a]" : "text-[var(--text-2)] hover:bg-[var(--panel-2)]/50"}`}
              data-testid="journal-view-calendar"
              aria-selected={view === "calendar"}
            >
              <CalendarDays size={15} /> Calendar
            </button>
          </div>
          {editable && (
            <Button variant="outline" onClick={() => setReminderOpen(true)} data-testid="reminder-settings-btn" className="bg-[var(--panel)]">
              <BellRing size={15} className="mr-1.5 text-[#c9a86a]" /> Reminders
            </Button>
          )}
          {editable && (
            <Button onClick={openNew} data-testid="add-article-btn" className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
              <Plus size={16} className="mr-2" /> New Article
            </Button>
          )}
        </div>
      </div>

      {view === "list" && (
      <>
      <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="journal-status-filter">
        <span className="text-xs uppercase tracking-wide text-[var(--muted)]">Filter</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-9 bg-[var(--panel)]" data-testid="journal-status-filter-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-[var(--muted)]">{filtered.length} shown</span>
      </div>
      <Card className="border-[var(--border-lch)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--panel-2)] text-[var(--text-2)] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Article</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-[var(--border-lch)] hover:bg-[var(--panel-2)]/50" data-testid={`article-row-${a.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-16 rounded bg-[var(--panel-2)] overflow-hidden flex items-center justify-center shrink-0">
                        {a.cover_image ? <img src={fileUrl(a.cover_image)} alt="" className="h-full w-full object-cover" /> : <BookOpen size={16} className="text-[var(--muted)]" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => editable && openEdit(a)} className="font-medium text-left hover:text-[#9a7b3f] truncate">{a.title}</button>
                          {a.featured && <Star size={13} className="text-[#c9a86a] fill-[#c9a86a] shrink-0" />}
                        </div>
                        <div className="text-xs text-[var(--muted)]">{a.reading_time || 1} min · /{a.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[var(--text-2)]">{a.category || "—"}</td>
                  <td className="p-3">
                    <div className="flex flex-col items-start gap-1.5">
                      <Badge variant="outline" className={STATUS_BADGE[a.status] || STATUS_BADGE.draft}>
                        {STATUS_LABEL[a.status] || "Draft"}
                      </Badge>
                      {deliveryByArticle[a.id] && (
                        <DeliveryStatusChip delivery={deliveryByArticle[a.id]} articleId={a.id} />
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-[var(--text-2)] text-xs">
                    {a.status === "scheduled" && a.publish_at ? (
                      <span className="flex items-center gap-1 text-[#9a7b3f]" data-testid={`article-scheduled-at-${a.id}`}>
                        <CalendarClock size={12} /> {fmtDate(a.publish_at)}
                      </span>
                    ) : a.published_at ? fmtDate(a.published_at) : "—"}
                  </td>
                  <td className="p-3 text-right">
                    {editable ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`article-actions-${a.id}`}><MoreHorizontal size={18} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => openEdit(a)}>Edit Article</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => togglePublish(a)} data-testid={`toggle-publish-${a.id}`}>
                            {a.status === "published" ? <><EyeOff size={14} className="mr-2" />Move to Draft</> : <><Eye size={14} className="mr-2" />Publish</>}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleFeatured(a)}>{a.featured ? "Remove Featured" : "Mark Featured"}</DropdownMenuItem>
                          {a.status === "published" && (
                            <DropdownMenuItem onClick={() => window.open(`/journal/${a.slug}`, "_blank")}><ExternalLink size={14} className="mr-2" />View Live</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => setToDelete(a)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : <span className="text-xs text-[var(--muted)]">View only</span>}
                  </td>
                </tr>
              ))}
              {!loading && !filtered.length && (
                <tr><td colSpan={5} className="p-8 text-center text-[var(--muted)]">{statusFilter === "all" ? "No articles yet. Write your first guide." : `No ${STATUS_LABEL[statusFilter].toLowerCase()} articles.`}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}

      {view === "calendar" && (
        <ScheduleCalendar
          scheduledItems={scheduledItems}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          editable={editable}
          onReschedule={rescheduleArticle}
          onBulkReschedule={bulkReschedule}
          onEdit={openEdit}
          onPublishNow={togglePublish}
        />
      )}

      {/* Editor */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {form && (
            <>
              <DialogHeader><DialogTitle>{form.id ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-[var(--panel)]" data-testid="article-title" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Category</Label>
                    <Select value={form.category} onValueChange={(v) => set("category", v)}>
                      <SelectTrigger className="bg-[var(--panel)]" data-testid="article-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{JOURNAL_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Author</Label><Input value={form.author ?? ""} onChange={(e) => set("author", e.target.value)} className="bg-[var(--panel)]" /></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Excerpt</Label><Textarea rows={2} value={form.excerpt ?? ""} onChange={(e) => set("excerpt", e.target.value)} className="bg-[var(--panel)]" data-testid="article-excerpt" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Tags (comma-separated)</Label><Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="rolex, collecting, buying" className="bg-[var(--panel)]" /></div>

                <div className="space-y-2">
                  <Label className="text-xs text-[var(--text-2)]">Cover Image</Label>
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-20 rounded bg-[var(--panel-2)] overflow-hidden shrink-0 border border-[var(--border-lch)]">{form.cover_image && <img src={form.cover_image} alt="" className="h-full w-full object-cover" />}</div>
                    <Input value={form.cover_image ?? ""} onChange={(e) => set("cover_image", e.target.value)} placeholder="Image URL" className="bg-[var(--panel)] flex-1" />
                  </div>
                  <FileUpload visibility="public" category="journal" label="Upload Cover" onUploaded={(fs) => fs[0] && set("cover_image", fs[0].url)} />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Body</Label>
                  <RichTextEditor value={form.body} onChange={(v) => set("body", v)} testId="article-body" />
                  <p className="text-[11px] text-[var(--muted)]">Use the toolbar for headings, bold, lists, quotes and links.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 border-t border-[var(--border-lch)] pt-4">
                  <div className="space-y-1.5"><Label className="text-xs">SEO Title</Label><Input value={form.seo_title ?? ""} onChange={(e) => set("seo_title", e.target.value)} className="bg-[var(--panel)]" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">SEO Description</Label><Input value={form.seo_description ?? ""} onChange={(e) => set("seo_description", e.target.value)} className="bg-[var(--panel)]" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Status</Label>
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger className="bg-[var(--panel)]" data-testid="article-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Featured</Label><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="article-featured" /></div>
                </div>

                {form.status === "scheduled" && (
                  <div className="space-y-1.5 rounded-md border border-[var(--border-lch)] bg-[var(--panel)] p-3" data-testid="article-schedule-panel">
                    <Label className="text-xs flex items-center gap-1.5 text-[var(--text-2)]"><CalendarClock size={14} className="text-[#c9a86a]" /> Publish date &amp; time</Label>
                    <Input
                      type="datetime-local"
                      value={isoToLocalInput(form.publish_at)}
                      onChange={(e) => set("publish_at", localInputToIso(e.target.value))}
                      className="bg-[var(--panel-2)] w-auto"
                      data-testid="article-publish-at"
                    />
                    <div className="pt-2 space-y-1.5">
                      <Label className="text-xs flex items-center gap-1.5 text-[var(--text-2)]"><BellRing size={13} className="text-[#c9a86a]" /> Go-live reminder</Label>
                      <Select
                        value={form.reminder_lead_minutes == null ? "default" : String(form.reminder_lead_minutes)}
                        onValueChange={(v) => set("reminder_lead_minutes", v === "default" ? null : Number(v))}
                      >
                        <SelectTrigger className="bg-[var(--panel-2)] w-auto" data-testid="article-reminder-lead"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Use default</SelectItem>
                          {LEAD_CHOICES.map((c) => <SelectItem key={c.v} value={String(c.v)}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-[11px] text-[var(--muted)]">Saved as a draft now and published automatically at the chosen time (your local time). Stays hidden from the public until then — you can reschedule or move it back to Draft any time.</p>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center mt-4 gap-2">
                <Button variant="outline" onClick={openSaveTpl} data-testid="article-save-as-template">
                  <BookmarkPlus size={15} className="mr-1.5 text-[#c9a86a]" /> Save as template
                </Button>
                <Button onClick={save} data-testid="article-save" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]">{form.id ? "Save Changes" : "Create Article"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.title} will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="article-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReminderSettings open={reminderOpen} onOpenChange={setReminderOpen} />
      <TemplatePicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={applyTemplate} />

      {/* Save current article layout as a reusable template */}
      <Dialog open={saveTplOpen} onOpenChange={(o) => !o && setSaveTplOpen(false)}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookmarkPlus size={18} className="text-[#c9a86a]" /> Save as template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[var(--muted)]">Saves this article's category, summary and body layout as a reusable starting point for all editors. It won't include tags or the schedule.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Template name</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Collector's Guide" className="bg-[var(--panel)]" data-testid="save-template-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description <span className="text-[var(--muted)]">(optional)</span></Label>
              <Textarea rows={2} value={tplDesc} onChange={(e) => setTplDesc(e.target.value)} placeholder="A short note on when to use this layout" className="bg-[var(--panel)]" data-testid="save-template-description" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setSaveTplOpen(false)} data-testid="save-template-cancel">Cancel</Button>
            <Button onClick={saveAsTemplate} disabled={savingTpl || !tplName.trim()} className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]" data-testid="save-template-confirm">
              {savingTpl ? "Saving…" : "Save template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
